import React from 'react';
import {List} from '../List/List';
import {ListItem} from '../List/ListItem';
import {FooterListTitle} from './FooterListTitle';
import {styled} from '@mui/material/styles';
import {excludeComponentProps} from '../utils/excludeComponentProps';

import type {ListProps} from '../List/List';
import type {FooterListTitleProps as FLTProps} from './FooterListTitle';

export interface FooterListProps extends Omit<ListProps, 'dense' | 'subheader' | 'disablePadding'> {
    /**
     * Used for vertical Footer Links
     */
    column?: boolean;
    /**
     * Takes a value that's converted to rem. 1 unit
     * of spacing is the equivalent of 0.25rem;
     */
    spacing?: number;
    /**
     * Title for the generated FooterListTitle.
     */
    title?: string;
    /**
     * Exposing FooterListTitle props
     */
    FooterListTitleProps?: FLTProps;
}

const StyledList = styled(List, {
    ...excludeComponentProps('column', 'spacing')
})<FooterListProps>(({
    column, spacing
}) => {
    const pRem = spacing && spacing / 4;

    return {
        width: '100%',

        ...!column && {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',

            li: {
                width: 'auto',
                marginLeft: `${pRem}rem`,
                marginRight: `${pRem}rem`
            }
        }
    };
});

export const FooterList = React.forwardRef<any, FooterListProps>(({
    children, column = false, title, spacing = 2,
    FooterListTitleProps, ...props
}, ref): React.ReactElement => (
    <StyledList
        column={column}
        dense
        disablePadding
        ref={ref}
        spacing={spacing}
        {...props}
    >
        {title &&
        <ListItem disableGutters>
            <FooterListTitle
                text={title}
                {...FooterListTitleProps}
            />
        </ListItem>
        }
        { React.Children.map<React.ReactNode, React.ReactNode>(children, (child, index): React.ReactElement => (
            <ListItem
                disableGutters
                key={`list-item-${index}`}
            >{child}</ListItem>
        ))}
    </StyledList>
));

FooterList.displayName = 'FooterList';
